import React from 'react';
import {Toggle} from '@rocket-ui/components';
import {
	useFetchAutoApplicationSetting,
	useUpdateAutoApplicationSetting,
} from '@rocket-ui/lk-api';
import {useIsMaxSm} from 'features/auth/ui/AuthorizationSteps/useIsMaxSm';
import {Drawer} from 'shared/ui/Drawer';
import {
	formatAutoReplyAddress,
	formatAutoReplyPaymentFromRubles,
	getAutoReplyApiErrorMessage,
	getAutoReplyCompanyName,
	locationsFromAddressInput,
	normalizeAutoReplyLocations,
	type AutoReplyLocation,
} from '../../model/autoReplyCompanies';
import styles from './AutoReplySettingsDrawer.module.css';

const MIN_REWARD_RUBLES = 100;
const MAX_REWARD_RUBLES = 500_000;
const REWARD_STEP_RUBLES = 100;
const ADDRESS_PLACEHOLDER = 'г. Москва, г. Санкт-Петербург';
const SERVICE_TYPE_PLACEHOLDER = 'Доставка, упаковка, уборка, готовка';

export type AutoReplySettingsDrawerProps = {
	settingId: number | null;
	previewName?: string;
	onClose: () => void;
};

type FormState = {
	enabled: boolean;
	rewardRubles: number;
	remote: boolean;
	address: string;
	serviceType: string;
	locations: AutoReplyLocation[];
	currency: string;
	customerId?: number;
};

function rublesFromCents(cents?: number): number {
	if (cents == null) return MIN_REWARD_RUBLES;
	return Math.min(MAX_REWARD_RUBLES, Math.max(MIN_REWARD_RUBLES, Math.round(cents / 100)));
}

function WrappingTextField({
	id,
	label,
	value,
	placeholder,
	disabled,
	onChange,
}: {
	id: string;
	label: string;
	value: string;
	placeholder: string;
	disabled?: boolean;
	onChange: (value: string) => void;
}): React.ReactElement {
	return (
		<div className={styles.field}>
			<label className={styles.label} htmlFor={id}>
				{label}
			</label>
			<div className={styles.growWrap} data-value={value || placeholder}>
				<textarea
					id={id}
					className={styles.fieldInput}
					placeholder={placeholder}
					value={value}
					disabled={disabled}
					rows={1}
					wrap="soft"
					autoComplete="off"
					onKeyDown={(event) => {
						if (event.key === 'Enter') {
							event.preventDefault();
						}
					}}
					onChange={(event) => onChange(event.target.value.replace(/[\r\n]/g, ''))}
				/>
			</div>
		</div>
	);
}

export function AutoReplySettingsDrawer({
	settingId,
	previewName,
	onClose,
}: AutoReplySettingsDrawerProps): React.ReactElement {
	const isMaxSm = useIsMaxSm();
	const open = settingId != null;
	const {data, isLoading, isError} = useFetchAutoApplicationSetting(settingId ?? 0, {enabled: open});
	const {mutateAsync: updateSetting, isLoading: isSaving} = useUpdateAutoApplicationSetting();

	const [form, setForm] = React.useState<FormState | null>(null);
	const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
	const initializedForId = React.useRef<number | null>(null);

	React.useEffect(() => {
		if (settingId == null) {
			initializedForId.current = null;
			setForm(null);
			setErrorMessage(null);
			return;
		}

		if (!data || (data.id != null && data.id !== settingId)) {
			if (initializedForId.current !== settingId) {
				setForm(null);
			}
			return;
		}

		if (initializedForId.current === settingId) {
			return;
		}

		const locations = normalizeAutoReplyLocations(data.locations);

		initializedForId.current = settingId;
		setForm({
			enabled: data.enabled === true,
			rewardRubles: rublesFromCents(data.minAmount?.amountCents),
			remote: data.remote === true,
			address: formatAutoReplyAddress(locations),
			serviceType: '',
			locations,
			currency: data.minAmount?.currency ?? 'RUB',
			customerId: data.customer?.id,
		});
		setErrorMessage(null);
	}, [data, settingId]);

	const title = data ? getAutoReplyCompanyName(data) : previewName || 'Автоотклик';
	const isBusy = isSaving;
	const isMinReward = (form?.rewardRubles ?? MIN_REWARD_RUBLES) <= MIN_REWARD_RUBLES;

	const handleSave = async () => {
		if (settingId == null || !form || isBusy) {
			return;
		}

		setErrorMessage(null);

		try {
			await updateSetting({
				id: settingId,
				body: {
					customerId: form.customerId,
					enabled: form.enabled,
					remote: form.remote,
					minAmount: {
						amountCents: form.rewardRubles * 100,
						currency: form.currency,
					},
					locations: locationsFromAddressInput(form.address, form.locations),
				},
			});
			onClose();
		} catch (error) {
			setErrorMessage(getAutoReplyApiErrorMessage(error, 'Не удалось сохранить настройки'));
		}
	};

	return (
		<Drawer
			open={open}
			onClose={onClose}
			title={<span className={styles.drawerTitle}>{title}</span>}
			showCloseButton
			closeButtonPosition="end"
			variant={isMaxSm ? 'fullscreen' : 'side'}
			bodyClassName={styles.drawerBody}
			footerClassName={styles.drawerFooter}
			footerContent={
				form ? (
					<div className={styles.footer}>
						{errorMessage ? <p className={styles.errorText}>{errorMessage}</p> : null}
						<button
							type="button"
							className={styles.saveButton}
							disabled={isBusy}
							onClick={() => {
								void handleSave();
							}}
						>
							Сохранить изменения
						</button>
					</div>
				) : null
			}
		>
			{isLoading && !form ? <p className={styles.stateText}>Загрузка...</p> : null}
			{isError && !form ? <p className={styles.errorText}>Не удалось загрузить настройки</p> : null}

			{form ? (
				<div className={styles.body}>
					<div className={styles.toggleRow}>
						<span className={styles.toggleLabel}>Автоотклик</span>
						<Toggle
							value={form.enabled}
							onChange={(enabled) => setForm((current) => (current ? {...current, enabled} : current))}
							disabled={isBusy}
						/>
					</div>

					{form.enabled ? (
						<>
							<div className={styles.paymentField}>
								<span className={styles.label}>Оплата</span>
								<p className={styles.paymentValue}>
									<span className={isMinReward ? styles.paymentValueMuted : undefined}>
										{formatAutoReplyPaymentFromRubles(form.rewardRubles)}
									</span>
								</p>
								<input
									type="range"
									className={styles.slider}
									min={MIN_REWARD_RUBLES}
									max={MAX_REWARD_RUBLES}
									step={REWARD_STEP_RUBLES}
									value={form.rewardRubles}
									aria-label="Оплата от"
									disabled={isBusy}
									onChange={(event) =>
										setForm((current) =>
											current ? {...current, rewardRubles: Number(event.target.value)} : current,
										)
									}
									style={
										{
											'--slider-progress': `${((form.rewardRubles - MIN_REWARD_RUBLES) / (MAX_REWARD_RUBLES - MIN_REWARD_RUBLES)) * 100}%`,
										} as React.CSSProperties
									}
								/>
							</div>

							<WrappingTextField
								id="auto-reply-address"
								label="Адрес (в радиусе 30 км)"
								placeholder={ADDRESS_PLACEHOLDER}
								value={form.address}
								disabled={isBusy}
								onChange={(address) =>
									setForm((current) => (current ? {...current, address} : current))
								}
							/>

							<div className={styles.toggleRow}>
								<span className={styles.toggleLabel}>Удалённо</span>
								<Toggle
									value={form.remote}
									onChange={(remote) => setForm((current) => (current ? {...current, remote} : current))}
									disabled={isBusy}
								/>
							</div>

							<WrappingTextField
								id="auto-reply-service-type"
								label="Тип услуги"
								placeholder={SERVICE_TYPE_PLACEHOLDER}
								value={form.serviceType}
								disabled={isBusy}
								onChange={(serviceType) =>
									setForm((current) => (current ? {...current, serviceType} : current))
								}
							/>
						</>
					) : null}
				</div>
			) : null}
		</Drawer>
	);
}
